import { msg } from "@lingui/core/macro";
import { createMemo, type JSX, Show } from "solid-js";
import { Lock } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, PageContainer } from "@fleet/ui";
import { api, createApiQuery } from "@/lib/api";
import { useSession } from "@/lib/auth";
import {
  billingQueryKeys,
  type components,
  featureEnabled,
  type TierFeature,
} from "@jornada/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@tanstack/solid-router";

type ErrorModel = components["schemas"]["ErrorModel"];
type BillingConfig = components["schemas"]["BillingConfigResponse"];
type BillingUsage = components["schemas"]["BillingUsageResponse"];

/**
 * B2B feature-flag gate (MONETIZATION_QUOTAS): wraps a surface
 * (e.g. team reports) and, ONLY with monetization ON and the feature
 * outside the effective tier, substitutes it with an upsell to /profile/plan.
 *
 * With monetization OFF (default), while config is loading, or if config
 * fails, it renders children as-is: zero behavior change.
 * HARD enforcement of server-side features (e.g. time-tracker bridge)
 * lives in the server (402); this is the UX layer.
 */
export function FeatureGate(props: { feature: TierFeature; children: JSX.Element }) {
  const { t } = useI18n();
  const session = useSession();

  const configQuery = createApiQuery<BillingConfig, ErrorModel>(
    () => billingQueryKeys.config(),
    async () => api.GET("/api/billing/config", {}),
    () => ({ staleTime: 30_000 }),
  );

  const usageQuery = createApiQuery<BillingUsage, ErrorModel>(
    () => billingQueryKeys.usage(),
    async () => api.GET("/api/billing/usage", {}),
    () => ({
      staleTime: 15_000,
      enabled: !session.isVisitor() && configQuery.isSuccess && configQuery.data.enabled,
    }),
  );

  const locked = createMemo(() => {
    const config = configQuery.isSuccess ? configQuery.data : undefined;
    if (!config?.enabled) {
      return false; // OFF / loading / error => historical behavior.
    }
    if (usageQuery.isLoading && !session.isVisitor()) {
      return false; // no parpadear el candado mientras resolvemos el tier.
    }
    const tier = usageQuery.isSuccess ? (usageQuery.data?.tier ?? undefined) : undefined;
    return !featureEnabled(
      {
        enabled: config.enabled,
        paymentsEnabled: config.paymentsEnabled,
        provider: config.provider,
        defaultTier: config.defaultTier,
        tiers: config.tiers.map((tierDef) => ({
          id: tierDef.id,
          label: tierDef.label,
          adsEnabled: tierDef.adsEnabled,
          features: tierDef.features ?? [],
          limits: {
            storage_bytes: tierDef.limits.storageBytes ?? null,
            items: tierDef.limits.maxItems ?? null,
            collections: tierDef.limits.maxCollections ?? null,
            orgs: tierDef.limits.maxOrgs ?? null,
            attachments: tierDef.limits.maxAttachments ?? null,
          },
        })),
      },
      tier,
      props.feature,
    );
  });

  const featureTitle = () =>
    props.feature === "team_reports"
      ? t(msg`Team reports are a Team feature`)
      : t(msg`Time-tracker integration is a Team feature`);

  return (
    <Show
      when={!locked()}
      fallback={
        <PageContainer maxWidth="2xl">
          <Card data-testid={`feature-locked-${props.feature}`}>
            <CardContent class="p-8 flex flex-col items-center gap-3 text-center">
              <Lock class="h-8 w-8 text-muted-foreground" />
              <p class="font-semibold">{featureTitle()}</p>
              <p class="text-sm text-muted-foreground">
                {t(msg`Upgrade your plan to unlock it for your whole team.`)}
              </p>
              <Link to="/profile/plan">
                <Button size="sm" data-testid="feature-locked-upgrade">
                  {t(msg`View plans`)}
                </Button>
              </Link>
            </CardContent>
          </Card>
        </PageContainer>
      }
    >
      {props.children}
    </Show>
  );
}
